import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {FieldError} from '@/components/field-error'
import {EVIDENCE_ACCEPT, uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import type {UploadTarget} from '@/hooks/sgsst/evidencias/use-evidence'
import {type LicenciaCategoria} from '@/hooks/inci/consola/use-licencia'
import {useLicenciaFieldErrors} from '@/hooks/common/use-licencia-field-errors'
import {useLicenciaFilePicker} from '@/hooks/common/use-licencia-file-picker'
import {translateApiError} from '@/lib/errors'
import {tope} from '@/lib/campos-texto'

const LICENCIA_CATEGORIAS: LicenciaCategoria[] = ['tecnico', 'tecnologo', 'profesional', 'posgrado']

type PersonaLicencia = {
  numero: string | null
  categoria: string | null
  vigencia: string | null
  estado: string | null
  rechazo_motivo: string | null
  download_url: string | null
}

export type PersonaLicenciaTarget = {
  userId: string
  name: string
  licencia: PersonaLicencia
}

type PersonaLicenciaDialogProps = {
  target: PersonaLicenciaTarget | null
  tKey: string
  testPrefix: string
  onClose: () => void
  mintUploadTarget: (userId: string, file: File) => Promise<UploadTarget>
  onSave: (input: {
    userId: string
    numero: string
    categoria: LicenciaCategoria
    vigencia: string
    doc_key: string
  }) => Promise<unknown>
  /** Una promesa (`mutateAsync`) para que el diálogo muestre el fallo. */
  onVerify: (userId: string) => Promise<unknown>
  saving: boolean
  verifying: boolean
}

/** Mutations are injected: the admins and partners consoles hit different endpoints. */
export function PersonaLicenciaDialog(props: PersonaLicenciaDialogProps) {
  const {target, tKey, testPrefix, onClose} = props
  const {t} = useTranslation()

  return (
    <Dialog open={!!target} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid={`${testPrefix}-licencia-dialog`}>
        <DialogHeader>
          <DialogTitle>{t(`${tKey}.title`, {name: target?.name ?? ''})}</DialogTitle>
        </DialogHeader>
        {target && <LicenciaForm {...props} target={target} />}
      </DialogContent>
    </Dialog>
  )
}

/** Vive dentro de `DialogContent`, que se desmonta al cerrar: cada apertura arranca de la licencia guardada. */
function LicenciaForm({
  target,
  tKey,
  testPrefix,
  onClose,
  mintUploadTarget,
  onSave,
  onVerify,
  saving,
  verifying,
}: PersonaLicenciaDialogProps & {target: PersonaLicenciaTarget}) {
  const {t} = useTranslation()
  const [numero, setNumero] = useState(target.licencia.numero ?? '')
  const [categoria, setCategoria] = useState<LicenciaCategoria>(
    (target.licencia.categoria as LicenciaCategoria | null) ?? 'profesional'
  )
  const [vigencia, setVigencia] = useState(target.licencia.vigencia ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const licenciaFields = useLicenciaFieldErrors()
  const fileField = useLicenciaFilePicker(setError)

  const validateForm = () => licenciaFields.validate(numero, vigencia)

  async function submit(e: FormEvent) {
    e.preventDefault()
    const errors = validateForm()
    if (errors.numero || errors.vigencia) return
    if (!fileField.file) {
      setError(t(`${tKey}.fileRequired`))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const uploadTarget = await mintUploadTarget(target.userId, fileField.file)
      await uploadToStorage(uploadTarget, fileField.file, () => {})
      await onSave({
        userId: target.userId,
        numero: numero.trim(),
        categoria,
        vigencia,
        doc_key: uploadTarget.object_key,
      })
      onClose()
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setBusy(false)
    }
  }

  const estado = target.licencia.estado

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t(`${tKey}.description`)}</p>
      {estado === 'rechazada' && target.licencia.rechazo_motivo && (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {target.licencia.rechazo_motivo}
        </p>
      )}
      <form onSubmit={submit} className="space-y-4" noValidate>
        <div className="flex flex-col gap-4">
          <div className="space-y-2">
            <Label htmlFor={`${testPrefix}-lic-numero`}>{t(`${tKey}.numero`)}</Label>
            <Input
              id={`${testPrefix}-lic-numero`}
              data-testid={`${testPrefix}-lic-numero`}
              className="font-mono"
              value={numero}
              onChange={(e) => {
                setNumero(e.target.value)
                licenciaFields.clearNumero()
              }}
              onBlur={licenciaFields.blurNumero}
              maxLength={tope('LIC')}
              required
              aria-invalid={!!licenciaFields.fieldErrors.numero}
            />
            <FieldError error={licenciaFields.fieldErrors.numero} />
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${testPrefix}-lic-categoria`}>{t(`${tKey}.categoria`)}</Label>
            <select
              id={`${testPrefix}-lic-categoria`}
              data-testid={`${testPrefix}-lic-categoria`}
              value={categoria}
              onChange={(e) => setCategoria(e.target.value as LicenciaCategoria)}
              className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm">
              {LICENCIA_CATEGORIAS.map((c) => (
                <option key={c} value={c}>
                  {t(`licencia.categorias.${c}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor={`${testPrefix}-lic-vigencia`}>{t(`${tKey}.vigencia`)}</Label>
            <Input
              id={`${testPrefix}-lic-vigencia`}
              data-testid={`${testPrefix}-lic-vigencia`}
              type="date"
              className="font-mono"
              value={vigencia}
              onChange={(e) => {
                setVigencia(e.target.value)
                licenciaFields.clearVigencia()
              }}
              onBlur={licenciaFields.blurVigencia}
              required
              aria-invalid={!!licenciaFields.fieldErrors.vigencia}
            />
            <FieldError error={licenciaFields.fieldErrors.vigencia} />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`${testPrefix}-lic-file`}>{t(`${tKey}.file`)}</Label>
          <input
            id={`${testPrefix}-lic-file`}
            data-testid={`${testPrefix}-lic-file`}
            type="file"
            accept={EVIDENCE_ACCEPT}
            onChange={(e) => fileField.pickFile(e.target.files?.[0])}
            className="block w-full text-sm text-muted-foreground file:mr-3 file:min-h-11 file:rounded-lg file:border-0 file:bg-secondary file:px-4 file:text-sm file:font-medium"
          />
          {target.licencia.download_url && (
            <a
              href={target.licencia.download_url}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-primary hover:underline">
              {t(`${tKey}.viewDocument`)}
            </a>
          )}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {estado === 'por_verificar' && (
            <Button
              type="button"
              variant="outline"
              data-testid={`${testPrefix}-lic-verify`}
              disabled={verifying}
              onClick={() => {
                setError(null)
                onVerify(target.userId).catch((err) => setError(translateApiError(err)))
              }}>
              {verifying ? t(`${tKey}.verifying`) : t(`${tKey}.verify`)}
            </Button>
          )}
          <Button type="submit" data-testid={`${testPrefix}-lic-save`} disabled={busy || saving}>
            {busy || saving ? t(`${tKey}.saving`) : t(`${tKey}.save`)}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t(`${tKey}.verifyHint`)}</p>
      </form>
    </div>
  )
}
